// @dynamic-i18n-keys
// Dynamic t() at line 51 (idx ? original : preset):
//   deviationSubmit.form.displayResolution.option.label.original
//   deviationSubmit.form.displayResolution.option.label.preset
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import AddWatermarkField from '../AddWatermarkField';
import { useSubmitField } from '../../hooks/useSubmitField';
import { useSubmitFormStatus } from '../../hooks/useSubmitFormStatus';
import { withConditionalField } from '../../hoc/withConditionalField';
import { SubmitFormContext } from '../../context';

import s from './DisplayOptionsField.scss';

export interface Props {
  className?: string;
  withWatermark?: boolean;
  isLabelHidden?: boolean;
}

const DisplayOptionsField: React.FC<Props> = ({
  className,
  withWatermark = true,
  isLabelHidden = true,
}) => {
  const { t } = useTranslation();

  // This field is rendered after papi init call so we are sure we have a response
  const { initResponse } = useContext(SubmitFormContext)!;
  const { formStatus, isMainUploading } = useSubmitFormStatus();
  const { setValue } = useFormContext();
  const { displayOptions } = initResponse!;
  const displayIndex = `${initResponse!.displayIndex ?? 0}`;

  const {
    id,
    field: { onChange, value },
  } = useSubmitField('displayResolution', {
    // Internally we keep the value as a string because SelectField gets in the way if not.
    // When submitting to papi we convert back to a number.
    initAdapter: ({ displayIndex: initValue }) => `${initValue ?? 0}`,
    submitAdapter: ({ displayResolution }) => ({
      display_resolution: Number(displayResolution),
    }),
  });

  // There's 9 display options identified with an integer. First option is "original size"
  const items = useMemo<DropdownOption[]>(
    () =>
      (displayOptions ?? []).map(({ width, height }, idx) => ({
        label: t(
          `deviationSubmit.form.displayResolution.option.label.${
            idx === 0 ? 'original' : 'preset'
          }`,
          {
            width,
            height,
          }
        ),
        value: `${idx}`,
        selected: value === `${idx}`,
      })),
    [t, displayOptions, value]
  );

  // Display options may change if a new image is uploaded. In that case we update the value to the default.
  useEffect(() => {
    setValue('displayResolution', displayIndex, {
      shouldDirty: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayOptions]);

  // Switching to original resolution turns off watermarking
  const handleChange = useCallback(
    newValue => {
      if (newValue === '0') {
        setValue('addWatermark', false, { shouldDirty: true });
      }
      onChange(newValue);
    },
    [setValue, onChange]
  );

  const isBusy = isMainUploading || formStatus === 'fetching';

  return (
    <div className={c(s['root'], className)}>
      <SelectField
        className={s['resolution']}
        id={id}
        label={t('deviationSubmit.form.displayResolution.label')}
        items={items}
        value={value}
        onSelect={handleChange}
        withBlankOption={false}
        disabled={isBusy}
        isLabelHidden={isLabelHidden}
      />
      {withWatermark && (
        <AddWatermarkField className={s['add-watermark-field']} />
      )}
    </div>
  );
};
DisplayOptionsField.displayName = 'DisplayOptionsField';

// Only render field when there's display options data available
export default withConditionalField(
  DisplayOptionsField,
  ({ displayOptions }) => !!displayOptions
);
